<div class="modal-production">
  <!-- Modal header -->
  <div class="modal-production__header">
    <h2 class="modal-production__header-title">
      {{ 'inventory.compoundArticles.modals.produce.title' | translate }}
    </h2>
  </div>

  <!-- Article information section -->
  <mat-card class="modal-production__article-card">
    <mat-card-header>
      <mat-card-title>
        {{
          'inventory.compoundArticles.modals.produce.articleInfo' | translate
        }}
      </mat-card-title>
    </mat-card-header>
    <mat-card-content>
      <div class="modal-production__article-details">
        <div class="modal-production__article-detail">
          <span class="modal-production__article-detail-label">
            {{
              'inventory.compoundArticles.modals.produce.articleCode'
                | translate
            }}
          </span>
          <span class="modal-production__article-detail-value">
            {{ compoundArticle()?.baseArticle?.codigo }}
          </span>
        </div>
        <div class="modal-production__article-detail">
          <span class="modal-production__article-detail-label">
            {{
              'inventory.compoundArticles.modals.produce.articleDescription'
                | translate
            }}
          </span>
          <span class="modal-production__article-detail-value">
            {{ compoundArticle()?.baseArticle?.descripcion }}
          </span>
        </div>
      </div>
    </mat-card-content>
  </mat-card>

  <!-- Main content with defer -->
  @defer (when !isLoading()) {
    <!-- Production configuration card -->
    <mat-card class="modal-production__production-card">
      <mat-card-header>
        <mat-card-title>
          {{
            'inventory.compoundArticles.modals.produce.productionSettings'
              | translate
          }}
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <!-- Section 1: Main warehouse and quantity to produce -->
        <div class="modal-production__warehouse-section">
          <div class="modal-production__warehouse-section-row">
            <div class="modal-production__warehouse-section-warehouse">
              <mat-form-field
                class="modal-production__warehouse-section-warehouse__dropdown"
              >
                <mat-label>{{
                  'inventory.compoundArticles.modals.produce.warehouse'
                    | translate
                }}</mat-label>
                <mat-select
                  [value]="selectedWarehouse()"
                  (selectionChange)="onWarehouseChange($event.value)"
                >
                  @for (
                    warehouse of allWarehouses();
                    track warehouse.id_almacen
                  ) {
                    <mat-option [value]="parseInt(warehouse.id_almacen)">
                      {{ warehouse.nombre }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>

            <div class="modal-production__warehouse-section-quantity">
              <mat-form-field
                class="modal-production__warehouse-section-quantity__field"
              >
                <mat-label>{{ quantityLabel() | translate }}</mat-label>
                <input
                  matInput
                  type="number"
                  step="1"
                  min="1"
                  [max]="maxQuantityAvailable()"
                  [formControl]="quantityControl"
                  [class.modal-production__warehouse-section-quantity__field--invalid]="
                    quantityControl.invalid
                  "
                />
                @if (isLoading()) {
                  <mat-hint>{{
                    'inventory.compoundArticles.modals.produce.calculatingCapacity'
                      | translate
                  }}</mat-hint>
                } @else if (
                  productionCapacity() && maxQuantityAvailable() !== null
                ) {
                  <mat-hint>{{
                    maxAvailableHintKey()
                      | translate: { quantity: maxQuantityAvailable()! }
                  }}</mat-hint>
                } @else {
                  <mat-hint>{{
                    'inventory.compoundArticles.modals.produce.noCapacityCalculated'
                      | translate
                  }}</mat-hint>
                }
              </mat-form-field>

              @if (quantityControl.invalid && quantityControl.touched) {
                <div
                  class="modal-production__warehouse-section-quantity__error"
                >
                  @if (quantityControl.errors?.['required']) {
                    {{
                      (shouldShowRecipeInfo()
                        ? 'inventory.compoundArticles.modals.produce.batchIsRequired'
                        : 'inventory.compoundArticles.modals.produce.quantityIsRequired'
                      ) | translate
                    }}
                  }
                  @if (quantityControl.errors?.['min']) {
                    {{
                      (shouldShowRecipeInfo()
                        ? 'inventory.compoundArticles.modals.produce.minimumBatchIs'
                        : 'inventory.compoundArticles.modals.produce.minimumQuantityIs'
                      ) | translate
                    }}
                  }
                  @if (quantityControl.errors?.['pattern']) {
                    {{
                      (shouldShowRecipeInfo()
                        ? 'inventory.compoundArticles.modals.produce.batchMustBeInteger'
                        : 'inventory.compoundArticles.modals.produce.quantityMustBeInteger'
                      ) | translate
                    }}
                  }
                  @if (
                    maxQuantityAvailable() !== null &&
                    quantityToProduce() > maxQuantityAvailable()!
                  ) {
                    {{
                      (shouldShowRecipeInfo()
                        ? 'inventory.compoundArticles.modals.produce.maximumBatchExceeded'
                        : 'inventory.compoundArticles.modals.produce.maximumQuantityExceeded'
                      ) | translate
                    }}
                  }
                </div>
              }
            </div>
          </div>

          <!-- Recipe information message -->
          @if (shouldShowRecipeInfo()) {
            <div class="modal-production__recipe-info">
              <mat-icon>info</mat-icon>
              <span>
                {{
                  'inventory.compoundArticles.modals.produce.recipeInfo'
                    | translate
                      : {
                          batchSize: recipeQuantity(),
                          totalQuantity: totalProductionQuantity(),
                          batches: quantityToProduce(),
                        }
                }}
              </span>
            </div>
          }
        </div>

        <!-- Section 2: Subarticles table -->
        <div class="modal-production__subarticles-section">
          <h3 class="modal-production__subarticles-section-title">
            {{
              'inventory.compoundArticles.modals.produce.subArticles'
                | translate
            }}:
          </h3>

          @defer (when componentSources().length > 0) {
            <table
              mat-table
              [dataSource]="componentSources()"
              class="modal-production__table"
            >
              <!-- Description column -->
              <ng-container matColumnDef="description">
                <th
                  mat-header-cell
                  *matHeaderCellDef
                  class="modal-production__table-header__cell"
                >
                  {{
                    'inventory.compoundArticles.modals.produce.description'
                      | translate
                  }}
                </th>
                <td
                  mat-cell
                  *matCellDef="let source"
                  class="modal-production__table-row__cell modal-production__table-row__cell--description"
                >
                  {{ source.articleDescription }}
                </td>
              </ng-container>

              <!-- Unit column -->
              <ng-container matColumnDef="unit">
                <th
                  mat-header-cell
                  *matHeaderCellDef
                  class="modal-production__table-header__cell"
                >
                  {{
                    'inventory.compoundArticles.modals.produce.unit' | translate
                  }}
                </th>
                <td
                  mat-cell
                  *matCellDef="let source"
                  class="modal-production__table-row__cell modal-production__table-row__cell--unit"
                >
                  @if (source.isLoadingUnits) {
                    <mat-icon class="modal-production__loading-spinner"
                      >hourglass_empty</mat-icon
                    >
                  } @else {
                    <mat-form-field subscriptSizing="dynamic">
                      <mat-select
                        [value]="source.selectedUnitId"
                        (selectionChange)="
                          onUnitChange(
                            source.id_component_article,
                            $event.value
                          )
                        "
                      >
                        @for (
                          unit of source.availableUnits;
                          track unit.id_unit
                        ) {
                          <mat-option [value]="unit.id_unit">
                            {{ unit.description }}
                          </mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  }
                </td>
              </ng-container>

              <!-- Available quantity column -->
              <ng-container matColumnDef="availableStock">
                <th
                  mat-header-cell
                  *matHeaderCellDef
                  class="modal-production__table-header__cell"
                >
                  {{
                    'inventory.compoundArticles.modals.produce.availableQuantity'
                      | translate
                  }}
                </th>
                <td
                  mat-cell
                  *matCellDef="let source"
                  [class]="
                    isStockInsufficient(
                      source.availableStock,
                      source.requiredQuantity
                    )
                      ? 'modal-production__table-row__cell modal-production__table-row__cell--stock-insufficient'
                      : 'modal-production__table-row__cell modal-production__table-row__cell--stock-sufficient'
                  "
                >
                  {{ source.availableStock | number: '1.0-10' }}
                </td>
              </ng-container>

              <!-- Required quantity column -->
              <ng-container matColumnDef="requiredStock">
                <th
                  mat-header-cell
                  *matHeaderCellDef
                  class="modal-production__table-header__cell"
                >
                  {{
                    'inventory.compoundArticles.modals.produce.requiredQuantity'
                      | translate
                  }}
                </th>
                <td
                  mat-cell
                  *matCellDef="let source"
                  class="modal-production__table-row__cell modal-production__table-row__cell--required"
                >
                  <mat-form-field subscriptSizing="dynamic">
                    <input
                      matInput
                      type="number"
                      step="any"
                      min="0"
                      [value]="
                        formatQuantity(
                          source.requiredQuantity * quantityToProduce()
                        )
                      "
                      (change)="
                        onRequiredQuantityChange(
                          source.id_component_article,
                          $event
                        )
                      "
                    />
                  </mat-form-field>
                </td>
              </ng-container>

              <!-- Source warehouse column -->
              <ng-container matColumnDef="sourceWarehouse">
                <th
                  mat-header-cell
                  *matHeaderCellDef
                  class="modal-production__table-header__cell"
                >
                  {{
                    'inventory.compoundArticles.modals.produce.sourceWarehouse'
                      | translate
                  }}
                </th>
                <td
                  mat-cell
                  *matCellDef="let source"
                  class="modal-production__table-row__cell modal-production__table-row__cell--warehouse"
                >
                  <mat-form-field subscriptSizing="dynamic">
                    <mat-select
                      [value]="source.id_warehouse"
                      (selectionChange)="
                        onComponentWarehouseChange(
                          source.id_component_article,
                          $event.value
                        )
                      "
                    >
                      @for (
                        warehouse of source.availableWarehouses;
                        track warehouse.id_almacen
                      ) {
                        <mat-option [value]="parseInt(warehouse.id_almacen)">
                          {{ warehouse.nombre }}
                        </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
            </table>
          } @placeholder {
            <div class="modal-production__table-placeholder">
              <mat-icon class="modal-production__loading-spinner"
                >hourglass_empty</mat-icon
              >
              <span>{{ 'global.loading' | translate }}</span>
            </div>
          }
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Section 3: Conditional warning message -->
    @if (showWarehouseWarning()) {
      <div class="modal-production__warning modal-production__warning--visible">
        <mat-icon class="modal-production__warning-icon">warning</mat-icon>
        <span class="modal-production__warning-text">
          {{
            'inventory.compoundArticles.modals.produce.differentWarehouseWarning'
              | translate
          }}
        </span>
      </div>
    }
  } @placeholder {
    <div class="modal-production__loading">
      <mat-icon class="modal-production__loading-spinner"
        >hourglass_empty</mat-icon
      >
      <span class="modal-production__loading-text">{{
        'global.loading' | translate
      }}</span>
    </div>
  } @error {
    <div class="modal-production__error">
      <mat-icon class="modal-production__error-icon">error</mat-icon>
      <span class="modal-production__error-message">{{ errorMessage() }}</span>
    </div>
  }

  <!-- Footer with buttons -->
  <div class="modal-production__footer">
    <div class="modal-production__footer-actions">
      <button
        mat-button
        type="button"
        class="modal-production__footer-actions__cancel"
        (click)="onCancel()"
      >
        {{ 'inventory.compoundArticles.modals.produce.cancel' | translate }}
      </button>

      <button
        mat-raised-button
        color="primary"
        type="button"
        class="modal-production__footer-actions__produce"
        [class.modal-production__footer-actions__produce--disabled]="
          !isFormValid() || isProducing()
        "
        [class.modal-production__footer-actions__produce--loading]="
          isProducing()
        "
        [disabled]="!isFormValid() || isProducing()"
        (click)="onProduce()"
      >
        <ng-container>
          @if (isProducing()) {
            <mat-icon>hourglass_empty</mat-icon>
            {{
              'inventory.compoundArticles.modals.produce.producing' | translate
            }}
          } @else {
            {{
              'inventory.compoundArticles.modals.produce.produceButton'
                | translate
            }}
          }
        </ng-container>
      </button>
    </div>
  </div>
</div>
